import { useQuery } from "@tanstack/react-query";
import { extractIdFromIri } from "../../utils/extractFromIri";

function parseWorkerBody(res) {
  if (typeof res?.body === "string") {
    try {
      return JSON.parse(res.body);
    } catch {
      return res.body;
    }
  }

  return res?.data ?? res ?? null;
}

function normalizePolicyRef(ref) {
  const rawRef = String(ref ?? "").trim();
  const id = extractIdFromIri(rawRef) || rawRef;

  if (!id) return null;

  const isTemplateRef =
    rawRef.includes("/policy_template/") ||
    rawRef.includes("/policy-templates/");

  return {
    key: id,
    endpoint: isTemplateRef
      ? `/policy-templates/${encodeURIComponent(id)}`
      : `/policies/${encodeURIComponent(id)}`,
  };
}

export function useGetContractsPoliciesByIds({
  callApiWithWorker,
  ids,
  enabled = true,
}) {
  const stablePolicyRefs = Array.isArray(ids)
    ? [...new Set(ids.filter(Boolean).map((id) => String(id).trim()))].sort()
    : [];

  return useQuery({
    queryKey: ["contractsPoliciesByIds", stablePolicyRefs.join("|")],
    enabled: Boolean(enabled && stablePolicyRefs.length),
    queryFn: async () => {
      const results = await Promise.all(
        stablePolicyRefs.map(async (policyRef) => {
          const normalizedRef = normalizePolicyRef(policyRef);
          if (!normalizedRef) return null;

          const res = await callApiWithWorker(normalizedRef.endpoint, {
            method: "GET",
          });

          return [normalizedRef.key, parseWorkerBody(res)];
        }),
      );

      return Object.fromEntries(results.filter(Boolean));
    },
    staleTime: 30_000,
  });
}
